import React, { useState, useEffect } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supportedLanguages, supportedLanguageCodes, defaultLanguage } from '../i18n/config';
import { useChangeLanguage } from './LanguageRouter';

// Inline SVG icons to avoid lucide-react import
const MenuIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="4" y1="12" x2="20" y2="12"/><line x1="4" y1="6" x2="20" y2="6"/><line x1="4" y1="18" x2="20" y2="18"/>
  </svg>
);
const XIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);
const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);

const Navbar = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const { lang } = useParams();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLangOpen, setIsLangOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const changeLanguage = useChangeLanguage();

  // Get current language from URL or fallback
  const currentLangCode = supportedLanguageCodes.includes(lang) ? lang : defaultLanguage;
  const currentLang = supportedLanguages.find(l => l.code === currentLangCode) || supportedLanguages[0];

  // Create localized path helper
  const localizedPath = (path) => `/${currentLangCode}${path}`;

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsLangOpen(false);
  }, [location]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (isLangOpen && !e.target.closest('[data-lang-dropdown]')) {
        setIsLangOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isLangOpen]);

  const navLinks = [
    { to: '', label: t('nav.home') },
    { to: '/experiences', label: t('nav.experiences') },
    { to: '/blog', label: 'Blog' },
    { to: '/about', label: t('nav.about') },
    { to: '/faq', label: t('nav.faq') },
    { to: '/contact', label: t('nav.contact') }
  ];

  const handleLanguageChange = (code) => {
    changeLanguage(code);
    setIsLangOpen(false);
  };

  // Check if current path matches (accounting for language prefix)
  const isActive = (path) => {
    const currentPath = location.pathname.replace(`/${currentLangCode}`, '') || '/';
    const targetPath = path || '/';
    return currentPath === targetPath;
  };

  const isHomepage = location.pathname === `/${currentLangCode}` || location.pathname === `/${currentLangCode}/`;

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled || !isHomepage 
            ? 'bg-white shadow-md' 
            : 'bg-white/95 backdrop-blur-sm'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <Link to={localizedPath('')} className="flex items-center gap-2 flex-shrink-0">
              <img 
                src="https://customer-assets.emergentagent.com/job_welcome-guests-6/artifacts/fp5dzp5a_logo%20cityredbus.png" 
                alt="City Red Bus" 
                className="h-10 md:h-12 w-auto"
                width={120}
                height={48}
                loading="eager"
                decoding="async"
              />
            </Link>

            {/* Desktop Navigation - Center */}
            <div className="hidden md:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={localizedPath(link.to)}
                  className={`text-sm font-medium transition-colors hover:text-brand-red ${
                    isActive(link.to) ? 'text-brand-red' : 'text-gray-700'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>

            {/* Right Side */}
            <div className="flex items-center gap-3">
              {/* Language Selector */}
              <div className="relative" data-lang-dropdown>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsLangOpen(!isLangOpen);
                  }}
                  className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-brand-red transition-colors p-2 rounded-full hover:bg-gray-100"
                  data-testid="language-selector"
                  aria-label="Select language"
                >
                  <span className="text-lg">{currentLang.flag}</span>
                  <span className="hidden sm:inline text-xs uppercase">{currentLang.code}</span>
                </button>
                
                {/* Language Dropdown */}
                <div
                  className={`absolute right-0 mt-2 bg-white rounded-xl shadow-xl border border-gray-200 py-2 w-56 z-50 max-h-80 overflow-y-auto transition-all duration-200 origin-top-right ${
                    isLangOpen ? 'opacity-100 scale-100' : 'opacity-0 scale-95 pointer-events-none'
                  }`}
                >
                  <div className="px-3 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-100 mb-1">
                    {i18n.language === 'it' ? 'Seleziona lingua' : 'Select Language'}
                  </div>
                  {supportedLanguages.map((langItem) => (
                    <button
                      key={langItem.code}
                      onClick={() => handleLanguageChange(langItem.code)}
                      className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-3 ${
                        currentLang.code === langItem.code ? 'bg-red-50 text-brand-red font-medium' : 'text-gray-700'
                      }`}
                      data-testid={`lang-${langItem.code}`}
                    >
                      <span className="text-lg">{langItem.flag}</span>
                      <span className="flex-1">{langItem.nativeName}</span>
                      {currentLang.code === langItem.code && (
                        <CheckIcon />
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Book CTA */}
              <Link
                to={localizedPath('/experiences')}
                className="hidden sm:inline-flex bg-brand-red text-white px-5 py-2.5 rounded-full text-sm font-medium hover:bg-brand-red-dark transition-colors shadow-sm"
                data-testid="book-now-btn"
              >
                {t('common.book_now')}
              </Link>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="md:hidden p-2 text-gray-700 hover:bg-gray-100 rounded-full"
                data-testid="mobile-menu-btn"
                aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
              >
                {isMobileMenuOpen ? <XIcon /> : <MenuIcon />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        <div
          className={`md:hidden bg-white border-t border-gray-100 overflow-hidden transition-all duration-300 ${
            isMobileMenuOpen ? 'max-h-[80vh] opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <div className="px-4 py-4 space-y-2">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={localizedPath(link.to)}
                className={`block px-4 py-3 rounded-xl text-base font-medium ${
                  isActive(link.to)
                    ? 'bg-brand-red/10 text-brand-red'
                    : 'text-gray-800 hover:bg-gray-50'
                }`}
              >
                {link.label}
              </Link>
            ))}
            
            {/* Language in Mobile */}
            <div className="px-4 py-3 border-t border-gray-100 mt-2 pt-4">
              <p className="text-sm text-gray-500 mb-3">
                {i18n.language === 'it' ? 'Lingua' : 'Language'}
              </p>
              <div className="grid grid-cols-3 gap-2">
                {supportedLanguages.slice(0, 6).map((langItem) => (
                  <button
                    key={langItem.code}
                    onClick={() => handleLanguageChange(langItem.code)}
                    className={`px-3 py-2 rounded-lg text-sm flex flex-col items-center gap-1 ${
                      currentLang.code === langItem.code 
                        ? 'bg-brand-red text-white' 
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <span className="text-lg">{langItem.flag}</span>
                    <span className="text-xs">{langItem.code.toUpperCase()}</span>
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-3 gap-2 mt-2">
                {supportedLanguages.slice(6).map((langItem) => (
                  <button
                    key={langItem.code}
                    onClick={() => handleLanguageChange(langItem.code)}
                    className={`px-3 py-2 rounded-lg text-sm flex flex-col items-center gap-1 ${
                      currentLang.code === langItem.code 
                        ? 'bg-brand-red text-white' 
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <span className="text-lg">{langItem.flag}</span>
                    <span className="text-xs">{langItem.code.toUpperCase()}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Book CTA Mobile */}
            <div className="px-4 pt-4">
              <Link
                to={localizedPath('/experiences')}
                className="block w-full bg-brand-red text-white text-center px-6 py-3 rounded-full text-base font-medium hover:bg-brand-red-dark transition-colors"
              >
                {t('common.book_now')}
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Spacer */}
      <div className="h-16 md:h-20" />
    </>
  );
};

export default Navbar;
